import { useCallback, useState } from "react";
import { FaBriefcase, FaChevronDown, FaHeadset, FaTruck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  COMMERCIAL_EMAIL,
  LOGISTICS_EMAIL,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_CLIPBOARD,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from "../containers/help/helpContactDetails";
import AppHeaderBar from "./AppHeaderBar";
import AppNavBar from "./AppNavBar";

const SECTION_COMMERCIAL = "commercial";
const SECTION_LOGISTICS = "logistics";
const SECTION_SUPPORT = "support";

function MobileHelpContactPanel({
  title,
  paragraphs,
  email,
  onCopyEmail,
  t,
  emailFooter,
  postalLabel,
  postalBody,
  phoneTel,
  phoneDisplay,
  onCopyPhone,
  phoneFooter,
}) {
  return (
    <div className="mobile-help-contact-panel">
      <h3 className="mobile-help-contact-panel-title">{title}</h3>
      {paragraphs.map((text) => (
        <p key={text} className="mobile-help-contact-panel-text">
          {text}
        </p>
      ))}
      <div className="mobile-help-contact-field">
        <span className="mobile-help-contact-field-label">
          {t("help_center_detail_email_label")}
        </span>
        <div className="mobile-help-contact-highlight">
          <a href={`mailto:${email}`} className="mobile-help-contact-link">
            {email}
          </a>
          <button
            type="button"
            className="mobile-help-contact-copy"
            onClick={() => onCopyEmail(email)}
            aria-label={t("help_center_copy_email_aria")}
          >
            <i className="fas fa-copy" aria-hidden />
          </button>
        </div>
        {emailFooter}
      </div>
      {postalBody && (
        <div className="mobile-help-contact-field">
          <span className="mobile-help-contact-field-label">{postalLabel}</span>
          <p className="mobile-help-contact-postal">{postalBody}</p>
        </div>
      )}
      {phoneDisplay && (
        <div className="mobile-help-contact-field">
          <span className="mobile-help-contact-field-label">
            {t("help_center_detail_phone_label")}
          </span>
          <div className="mobile-help-contact-highlight">
            <a href={phoneTel} className="mobile-help-contact-link">
              {phoneDisplay}
            </a>
            <button
              type="button"
              className="mobile-help-contact-copy"
              onClick={onCopyPhone}
              aria-label={t("help_center_copy_phone_aria")}
            >
              <i className="fas fa-copy" aria-hidden />
            </button>
          </div>
          {phoneFooter}
        </div>
      )}
    </div>
  );
}

const AppHelp = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState(null);

  const copyHelpEmail = useCallback(
    async (email) => {
      try {
        await navigator.clipboard.writeText(email);
        toast.success(t("help_center_email_copied"));
      } catch {
        toast.error(t("help_center_email_copy_failed"));
      }
    },
    [t],
  );

  const copyHelpPhone = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_PHONE_CLIPBOARD);
      toast.success(t("help_center_phone_copied"));
    } catch {
      toast.error(t("help_center_phone_copy_failed"));
    }
  }, [t]);

  const toggleSection = (sectionId) => {
    setOpenSection((current) => (current === sectionId ? null : sectionId));
  };

  const contactItems = [
    {
      id: SECTION_COMMERCIAL,
      icon: FaBriefcase,
      titleKey: "help_center_contact_commercial_title",
      whenKey: "help_center_contact_commercial_when",
      panelTitleKey: "help_center_contact_commercial_title",
      paragraphKeys: [
        "help_center_detail_commercial_lead",
        "help_center_detail_commercial_more",
      ],
      email: COMMERCIAL_EMAIL,
    },
    {
      id: SECTION_LOGISTICS,
      icon: FaTruck,
      titleKey: "help_center_contact_logistics_title",
      whenKey: "help_center_contact_logistics_when",
      panelTitleKey: "help_center_contact_logistics_title",
      paragraphKeys: [
        "help_center_detail_logistics_lead",
        "help_center_detail_logistics_more",
      ],
      email: LOGISTICS_EMAIL,
      postalLabelKey: "help_center_detail_postal_label",
      postalBodyKey: "help_center_contact_logistics_address",
    },
    {
      id: SECTION_SUPPORT,
      icon: FaHeadset,
      titleKey: "help_center_contact_support_title",
      whenKey: "help_center_contact_support_when",
      panelTitleKey: "help_center_contact_support_title",
      paragraphKeys: [
        "help_center_detail_support_lead",
        "help_center_detail_support_more",
      ],
      email: SUPPORT_EMAIL,
      emailFooterKey: "help_center_support_mail_tip",
      phone: true,
    },
  ];

  return (
    <div className="installation-list-page mobile-help-page">
      <AppHeaderBar
        title={t("mobile_support_menu_label")}
        setCookies={setCookies}
        saveUser={saveUser}
      />

      <main className="mobile-help-main">
        <section className="installation-subpage-card mobile-help-contact-card">
          <h2 className="mobile-help-section-title">
            {t("help_center_contact_title")}
          </h2>

          <div className="mobile-help-contact-list">
            {contactItems.map((item) => {
              const Icon = item.icon;
              const isOpen = openSection === item.id;

              return (
                <div key={item.id} className="mobile-help-contact-item">
                  <button
                    type="button"
                    className={
                      isOpen
                        ? "mobile-help-contact-trigger mobile-help-contact-trigger-open"
                        : "mobile-help-contact-trigger"
                    }
                    onClick={() => toggleSection(item.id)}
                    aria-expanded={isOpen}
                  >
                    <span
                      className="mobile-help-contact-trigger-icon"
                      aria-hidden
                    >
                      <Icon />
                    </span>
                    <span className="mobile-help-contact-trigger-body">
                      <span className="mobile-help-contact-trigger-title">
                        {t(item.titleKey)}
                      </span>
                      <span className="mobile-help-contact-trigger-when">
                        {t(item.whenKey)}
                      </span>
                    </span>
                    <FaChevronDown
                      className={
                        isOpen
                          ? "mobile-help-contact-trigger-chevron mobile-help-contact-trigger-chevron-open"
                          : "mobile-help-contact-trigger-chevron"
                      }
                      aria-hidden
                    />
                  </button>

                  {isOpen && (
                    <MobileHelpContactPanel
                      title={t(item.panelTitleKey)}
                      paragraphs={item.paragraphKeys.map((key) => t(key))}
                      email={item.email}
                      onCopyEmail={copyHelpEmail}
                      t={t}
                      emailFooter={
                        item.emailFooterKey ? (
                          <p className="mobile-help-contact-footnote">
                            {t(item.emailFooterKey)}
                          </p>
                        ) : null
                      }
                      postalLabel={
                        item.postalLabelKey ? t(item.postalLabelKey) : null
                      }
                      postalBody={
                        item.postalBodyKey ? t(item.postalBodyKey) : null
                      }
                      phoneTel={item.phone ? SUPPORT_PHONE_TEL : null}
                      phoneDisplay={item.phone ? SUPPORT_PHONE_DISPLAY : null}
                      onCopyPhone={copyHelpPhone}
                      phoneFooter={
                        item.phone ? (
                          <p className="mobile-help-contact-footnote">
                            {t("help_center_support_hours")}
                          </p>
                        ) : null
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <AppNavBar />
    </div>
  );
};

export default AppHelp;
